import type { TourSection } from './types';

// Query Lab es una herramienta avanzada — el copy explica conceptos
// asumiendo que el admin tiene contexto del sistema multi-fuente.

// El tab (Visual/JSON) es estado local del componente, no va en la URL: para
// apuntar a algo que vive adentro de uno hay que clickear esa pestaña
// primero. Visual es el modo por defecto (abre así la pantalla), así que
// cualquier paso que necesite el editor JSON tiene que pedirlo explícito —
// ya no alcanza con no tocar nada.
function openVisualTab(): () => Promise<void> {
  return async () => {
    const btn = document.querySelector<HTMLButtonElement>(
      '[data-tour="query-lab-tab-tree"]',
    );
    if (btn && btn.getAttribute('aria-selected') !== 'true') {
      btn.click();
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      );
    }
  };
}

function openJsonTab(): () => Promise<void> {
  return async () => {
    const btn = document.querySelector<HTMLButtonElement>(
      '[data-tour="query-lab-tab-json"]',
    );
    if (btn && btn.getAttribute('aria-selected') !== 'true') {
      btn.click();
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      );
    }
  };
}

export const productsQuerySections: TourSection[] = [
  {
    id: 'overview',
    label: 'Qué es Query Lab',
    steps: [
      {
        target: '[data-tour="query-lab-header"]',
        title: 'Query Lab',
        content:
          'Herramienta avanzada para probar consultas al catálogo eligiendo qué fuente de información usar para cada campo. Sirve para entender cómo se resuelve la información cuando hay varias fuentes (master, sap, axioma, vtex).',
      },
    ],
  },
  {
    id: 'sku-input',
    label: 'Buscar un producto puntual',
    steps: [
      {
        target: '[data-tour="query-lab-sku"]',
        title: 'Buscar por SKU',
        content:
          'Si pegas un SKU, la consulta busca ese producto puntual. Si lo dejas vacío, devuelve un listado paginado de productos.',
      },
    ],
  },
  {
    id: 'json-editor',
    label: 'Editor de la consulta',
    steps: [
      {
        target: '[data-tour="query-lab-json"]',
        title: 'Editor avanzado (JSON)',
        before: openJsonTab(),
        content:
          'Editas la consulta como texto JSON: qué campos pides y de qué fuentes. Las advertencias en rojo indican errores; el botón Ejecutar queda deshabilitado hasta resolverlos.',
      },
    ],
  },
  {
    id: 'tree-builder',
    label: 'Editor visual',
    steps: [
      {
        target: '[data-tour="query-lab-tree"]',
        title: 'Editor visual (árbol)',
        before: openVisualTab(),
        content:
          'Alternativa al JSON: armas la consulta haciendo click sobre los campos disponibles. Los cambios se reflejan en el JSON automáticamente.',
      },
    ],
  },
  {
    id: 'body-controls',
    label: 'Modo, área y filtros',
    steps: [
      {
        target: '[data-tour="query-lab-body-controls"]',
        title: 'Qué devuelve la consulta',
        before: openVisualTab(),
        content:
          'Arriba está lo que define la forma de la respuesta: si trae solo lo pedido o el producto completo, de qué área, qué fuentes se consultan y en qué orden. Los filtros que acotan qué productos entran están en el desplegable de abajo.',
      },
      {
        target: '[data-tour="query-lab-body-controls"]',
        title: 'Consultar un área',
        before: openVisualTab(),
        content:
          'Si eliges un área, el sistema usa la configuración de prioridad de ese cliente e ignora la prioridad y los campos que armes acá — por eso quedan deshabilitados. Sirve para ver exactamente lo mismo que ve esa integración.',
      },
      {
        target: '[data-tour="query-lab-body-controls"]',
        title: 'Filtrar por fuente',
        before: openVisualTab(),
        content:
          'Al marcar fuentes, solo se consultan esas: cada campo se resuelve con ellas y los productos que ninguna informó desaparecen del resultado. Un dato que exista solo en otra fuente se ve como faltante.',
      },
    ],
  },
  {
    id: 'sources-priority',
    label: 'Prioridad de fuentes',
    steps: [
      {
        target: '[data-tour="query-lab-json"]',
        title: 'Orden de fuentes',
        before: openJsonTab(),
        content:
          '"default" aplica a todos los campos sin orden específico. Para campos puntuales puedes definir un orden distinto (ej: nombre prioriza vtex, imagen prioriza master). El sistema busca en cada fuente en orden y devuelve la primera que tenga dato.',
      },
    ],
  },
  {
    id: 'run',
    label: 'Ejecutar consulta',
    steps: [
      {
        target: '[data-tour="query-lab-run"]',
        title: 'Ejecutar',
        content:
          'Corre la consulta con lo que tengas configurado. Mientras corre se ve un spinner; los resultados aparecen abajo con paginación si es listado.',
      },
    ],
  },
  {
    id: 'presets',
    label: 'Guardar consultas',
    steps: [
      {
        target: '[data-tour="query-lab-presets"]',
        title: 'Guardar y cargar',
        content:
          'Guarda consultas que uses seguido para no rearmarlas cada vez. Quedan en este navegador (no se sincronizan a otra computadora).',
      },
    ],
  },
];
